import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { Accessor } from 'solid-js';
import { CapabilityBase } from './capability-base';
import { SharedCapabilityProviderBase } from '@trimble/web3d-providers';
import { Web3DViewer } from '@technology/web3d';

/**
 * Capabilities that apply to multiple panes with shared state
 * and global UI control. E.g. measurements.
 */
export abstract class SharedCapabilityBase extends CapabilityBase {
  protected viewerWrappers: Accessor<Web3DViewerWrapper[]>;

  protected readonly viewerEventListeners = new Map<
    Web3DViewer,
    Array<{ event: string; handler: EventListener }>
  >();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected constructor(provider: SharedCapabilityProviderBase<any>) {
    super(provider);

    this.viewerWrappers = provider.viewerWrappers;
  }

  protected addViewerEventListener(
    viewer: Web3DViewer,
    event: string,
    handler: EventListener
  ): void {
    viewer.addEventListener(event, handler);
    const listeners = this.viewerEventListeners.get(viewer) || [];
    listeners.push({ event, handler });
    this.viewerEventListeners.set(viewer, listeners);
  }

  protected removeViewerEventListener(
    viewer: Web3DViewer,
    event: string,
    handler: EventListener
  ): void {
    viewer.removeEventListener(event, handler);
    const listeners = this.viewerEventListeners.get(viewer);
    if (listeners) {
      const index = listeners.findIndex(
        (l) => l.event === event && l.handler === handler
      );
      if (index !== -1) {
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.viewerEventListeners.delete(viewer);
        } else {
          this.viewerEventListeners.set(viewer, listeners);
        }
      }
    }
  }

  protected removeAllViewerEventListeners(viewer: Web3DViewer): void {
    const listeners = this.viewerEventListeners.get(viewer);
    if (listeners) {
      listeners.forEach(({ event, handler }) => {
        viewer.removeEventListener(event, handler);
      });
      this.viewerEventListeners.delete(viewer);
    }
  }

  protected clearAllViewerEventListeners(): void {
    this.viewerEventListeners.forEach((listeners, viewer) => {
      listeners.forEach(({ event, handler }) => {
        viewer.removeEventListener(event, handler);
      });
    });
    this.viewerEventListeners.clear();
  }
}
